import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { SelectedTreatments } from 'apps/sigma2-ui/src/components/SelectedTreatments';
import { SelectedTreatmentsVariant } from 'apps/sigma2-ui/src/components/SelectedTreatments';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';

interface TreatmentCartProps {
  show?: boolean;
  onClose: () => void;
}

export const TreatmentCart = ({ show, onClose }: TreatmentCartProps) => {
  const language = useLanguage();
  const { myObservation } = useObservation();

  const selectedTreatmentsCount =
    myObservation?.treatments?.filter((t) => t.status !== 'Available').length ??
    0;

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog className="font-sans fixed inset-0 z-overlay" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-1000 bg-opacity-50" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300 transform"
          enterFrom="opacity-0 translate-x-full"
          enterTo="opacity-100 translate-x-0"
          leave="ease-in duration-200 transform"
          leaveFrom="opacity-100 translate-x-0"
          leaveTo="opacity-0 translate-x-full"
        >
          <div
            className="fixed bg-white top-6 right-6 bottom-6 w-96 rounded-lg flex flex-col"
            data-qa="solutionsCart"
          >
            <Dialog.Title
              as="h3"
              data-qa="solutionCartHeading"
              style={{
                backgroundImage:
                  'radial-gradient(112.91% 137.83% at 115.91% 137.83%, #0A56A2 0%, #012C57 72.37%, #012C57 100%)',
              }}
              className="relative font-sans leading-6 font-normal text-lg p-6 text-white flex justify-between items-center rounded-t-lg"
            >
              {language.treatmentCartButton()}
              <button
                data-qa="treatmentSelectionCloseButton"
                onClick={onClose}
                className="inline-block p-1.5 hover:bg-blue-900 rounded-md bg-opacity-20 transition-colors translate-x-2"
              >
                <IconCloseSvg svg={{ className: 'text-white w-4' }} />
              </button>
            </Dialog.Title>
            <div className="p-6 text-xs relative overflow-y-auto flex-grow border-b-1 border-gray-200">
              <Dialog.Description as="p" className="text-gray-600 text-xs">
                {language.treatmentCartIntroBlurb()}
              </Dialog.Description>
              <SelectedTreatments variant={SelectedTreatmentsVariant.CART} />
            </div>
            <LinkButton
              data-qa="goToTreatmentCenterLink"
              disabled={selectedTreatmentsCount === 0}
              title={
                selectedTreatmentsCount > 0
                  ? language.treatmentCartCheckoutCta()
                  : language.treatmentCartCheckoutCtaDisabled()
              }
              variant={LegacyButtonVariant.PRIMARY_DARK}
              className="ml-2 inline-flex text-sm whitespace-nowrap font-sans self-center my-6"
              href={'/treatment-center'}
            >
              {language.treatmentCartCheckoutCta()}
            </LinkButton>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
